import { createSelector } from "@reduxjs/toolkit";
import { selectHttpIssuesOnBoard } from "../../issueSelectors";
import { IRootState } from "../../../../../store";
import { ISSUE_COLUMN_LABELS, ISSUE_DATAFILEDS_ENUM, ISSUE_DUEDATE_OPTIONS } from "../../issueConstants";
import { getDeeplyNestedValue } from "@mcapps/shared/sharedUtils";
import { selectHttpProjectMembers } from "../../../../projectSelectors";
import { IUserPartial } from "@schemas/issueSchema";

export interface IActiveIssueFilterOption {
  label: string;
  value: string;
  isActive: boolean;
}

export interface IActiveIssueFilter {
  label: string;
  filterKey: ISSUE_DATAFILEDS_ENUM;
  options: IActiveIssueFilterOption[];
}

export const makeSelectUniqueIssueFilterFromDataField = () =>
  createSelector(
    [
      selectHttpIssuesOnBoard,
      (_s: IRootState, field: ISSUE_DATAFILEDS_ENUM) => field,
      (_s: IRootState, _field: ISSUE_DATAFILEDS_ENUM, valueAndLabelMap: Record<string, string>) => valueAndLabelMap,
    ],
    (issuesOnBoard, field, valueAndLabelMap): IActiveIssueFilter => {
      const set = new Set<string>();

      issuesOnBoard.forEach((issue) => {
        const values = getDeeplyNestedValue(issue, field);

        if (Array.isArray(values)) {
          values.forEach((v) => {
            const str = String(v);
            if (str !== "") set.add(str);
          });
        } else if (values && typeof values === "object") {
          const id = getDeeplyNestedValue(values, "_id");
          if (id && String(id) !== "") set.add(String(id));
        } else if (typeof values === "string" && values !== "") {
          set.add(values);
        }
      });

      const options: IActiveIssueFilterOption[] = Array.from(set)
        .map((value) => ({
          label: valueAndLabelMap?.[value] ?? value,
          value,
          isActive: false,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      return {
        label: ISSUE_COLUMN_LABELS[field],
        filterKey: field,
        options,
      };
    },
  );

export const selectActiveTagFilters = (state: IRootState) => {
  const filters = state.issueReducerNew.dropdownFilters;
  return filters[ISSUE_DATAFILEDS_ENUM.TAGS] || [];
};

const EMPTY_FILTERS = [];

export const dropdownFiltersSelector = (s: IRootState) => s.issueReducerNew.dropdownFilters;
export const makeSelectActiveFiltersByField = () =>
  createSelector(
    [(s: IRootState) => s.issueReducerNew.dropdownFilters, (_s: IRootState, field: ISSUE_DATAFILEDS_ENUM) => field],
    (filters, field): string[] => filters?.[field] ?? EMPTY_FILTERS,
  );

export const selectAssignedToHashmap = createSelector([selectHttpIssuesOnBoard], (issuesOnBoard) => {
  const map: Record<string, string> = {};
  issuesOnBoard.forEach((issue) => {
    const assignedTo = issue[ISSUE_DATAFILEDS_ENUM.ASSIGNED_TO];
    if (assignedTo && typeof assignedTo === "object") {
      const id = getDeeplyNestedValue(assignedTo, "_id");
      const name = getDeeplyNestedValue(assignedTo, "name");
      if (id && name) {
        map[id] = name;
      }
    }
  });
  return map;
});

export const selectIssueDatesFiler = createSelector(
  [dropdownFiltersSelector],
  (dropdownFilters): IActiveIssueFilter => {
    return {
      filterKey: ISSUE_DATAFILEDS_ENUM.DATES_DUE,
      label: "Tidsfrist",
      options: ISSUE_DUEDATE_OPTIONS.map((t) => {
        return {
          label: t.value,
          value: t.value,
          isActive: dropdownFilters[ISSUE_DATAFILEDS_ENUM.DATES_DUE]?.includes(t.value) ?? false,
        };
      }),
    };
  },
);

export const selectUniqueAssignedInIssues = createSelector(
  [selectHttpIssuesOnBoard],
  (issuesOnBoard): IUserPartial[] => {
    const seen = new Map<string, IUserPartial>();

    for (const issue of issuesOnBoard) {
      const assignedTo = issue.assignedTo;
      if (assignedTo) {
        const id = assignedTo._id?.toString();
        if (!seen.has(id)) {
          seen.set(id, { _id: assignedTo._id, name: assignedTo.name });
        }
      }
    }

    return Array.from(seen.values());
  },
);

export const selectUniqueDisciplinesFromAssignedToInIssues = createSelector(
  [selectUniqueAssignedInIssues, selectHttpProjectMembers],
  (uniqueAssigned, projectMembers): string[] => {
    const disciplineSet = new Set<string>();

    // Skapa en snabb lookup: userId -> disciplin
    const memberMap = new Map<string, string>();
    for (const pm of projectMembers) {
      if (pm.disciplin) {
        memberMap.set(pm.user?._id?.toString(), pm.disciplin);
      }
    }

    // Gå igenom unika tilldelade användare
    for (const user of uniqueAssigned) {
      const disciplin = memberMap.get(user?._id?.toString());
      if (disciplin) {
        disciplineSet.add(disciplin);
      }
    }

    return Array.from(disciplineSet);
  },
);

export const selectDisciplineFilter = createSelector(
  [selectUniqueDisciplinesFromAssignedToInIssues, dropdownFiltersSelector],
  (uniqueDisciplines, dropdownFilters): IActiveIssueFilter => {
    return {
      filterKey: ISSUE_DATAFILEDS_ENUM.DISCIPLIN,
      label: "Ansv. Disiplin",
      options: uniqueDisciplines.map((discipline) => {
        return {
          label: discipline,
          value: discipline,
          isActive: dropdownFilters[ISSUE_DATAFILEDS_ENUM.DISCIPLIN]?.includes(discipline) ?? false,
        };
      }),
    };
  },
);

export const selectMineSakerFilter = createSelector(
  [dropdownFiltersSelector],
  (dropdownFilters): IActiveIssueFilter => {
    const optionsStatic: IActiveIssueFilterOption[] = [
      { label: "Jeg er eier", value: "owner._id", isActive: false },
      { label: "Opprettet av meg", value: "createdBy._id", isActive: false },
      { label: "Jeg er medlem", value: "member._id", isActive: false },
      { label: "Jeg er ansvarlig", value: "assigned._id", isActive: false },
    ].map((opt) => ({
      ...opt,
      isActive: dropdownFilters[ISSUE_DATAFILEDS_ENUM.MINE_SAKER]?.includes(opt.value) ?? false,
    }));

    return {
      label: ISSUE_COLUMN_LABELS[ISSUE_DATAFILEDS_ENUM.MINE_SAKER],
      filterKey: ISSUE_DATAFILEDS_ENUM.MINE_SAKER,
      options: optionsStatic,
    };
  },
);
